/**
 * How much metadata one indexed record may carry, and how it is measured.
 *
 * Pinecone caps a record's metadata at 40 KB and REJECTS the whole upsert
 * batch above it, so the size is decided on our side before any write. The
 * same measure and the same budget are used by the index-time writer, which
 * trims what it can, and by the API, which refuses a metadata patch that could
 * never fit. The measure is an estimate accepted as such (decision 2026-10-12):
 * the writer also counts the chunk body, which the index keeps outside the
 * cap, so it may trim a little early; it never writes a record the index would
 * refuse on size.
 *
 * @see https://docs.pinecone.io/guides/index-data/indexing-overview#metadata
 */
export const PINECONE_METADATA_LIMIT_BYTES = 40 * 1024;

/**
 * Headroom left under the cap. The record's `_id` and Pinecone's own accounting
 * are not part of the serialized metadata, and the cap is documented in round
 * numbers rather than as an exact serialisation rule, so the budget is
 * deliberately a little under it.
 */
export const METADATA_HEADROOM_BYTES = 2 * 1024;

/** The bytes a record's metadata may serialize to; the writer's budget and the API's ceiling. */
export const DOCUMENT_METADATA_BUDGET_BYTES =
  PINECONE_METADATA_LIMIT_BYTES - METADATA_HEADROOM_BYTES;

/**
 * The size as the index will see it: UTF-8 bytes of the JSON serialization.
 * JSON escaping is not size-preserving (a quote or a backslash is two bytes, a
 * control character six), so the raw string length is the wrong unit.
 */
export function metadataByteSize(metadata: unknown): number {
  return new TextEncoder().encode(JSON.stringify(metadata)).length;
}

/** The message every door prints when metadata serializes past {@link DOCUMENT_METADATA_BUDGET_BYTES}. */
export const DOCUMENT_METADATA_SIZE_RULE = `a document's metadata serializes to at most ${DOCUMENT_METADATA_BUDGET_BYTES} bytes of JSON`;
